import React, { useEffect, useMemo, useState } from 'react';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { AdminTabs } from './VoiceAdmin';

type DeliveryMethod = 'local' | 'fcm-remote' | 'email' | 'scheduled-function';
type NotificationCategory = 'practice' | 'face-off' | 'progress' | 'subscription' | 'system';

type NotificationSequenceRow = {
  id: string;
  name: string;
  trigger: string;
  title: string;
  body: string;
  category: NotificationCategory;
  deliveryMethod: DeliveryMethod;
  source: string;
  dataKeys: string[];
  opensInto: string;
  configDocId?: string;
  notes?: string;
};

type BannerMessage = {
  type: 'success' | 'error' | 'info';
  text: string;
};

const NOTIFICATION_SEQUENCES: NotificationSequenceRow[] = [
  {
    id: 'parrot-practice-window-local',
    name: 'Practice Window Reminder',
    trigger: 'Local daily reminder near the end of each selected practice window.',
    title: 'Morning speech?',
    body: '60 seconds to start the day with a focused practice speech.',
    category: 'practice',
    deliveryMethod: 'local',
    source: 'iOS - NotificationsService.scheduleDaily(windows:)',
    dataKeys: ['identifier: parrot.window.{window}'],
    opensInto: 'Practice tab',
    configDocId: 'parrot-practice-window-local',
    notes: 'Copy varies by Morning, Midday, Evening.',
  },
  {
    id: 'parrot-faceoff-waiting-room',
    name: 'Face-off Waiting Room Push',
    trigger: 'When one participant enters the waiting room and the other participant has a saved FCM token.',
    title: 'Your face-off is ready',
    body: '{{displayName}} is waiting. Tap to start.',
    category: 'face-off',
    deliveryMethod: 'fcm-remote',
    source: 'Firebase Function - sendFaceOffWaitingRoomPush',
    dataKeys: ['type', 'challengeId', 'token', 'deepLink', 'url'],
    opensInto: 'Face-off waiting room',
    configDocId: 'parrot-faceoff-waiting-room',
    notes: 'Writes delivery attempts to notification-logs.',
  },
  {
    id: 'parrot-faceoff-started',
    name: 'Face-off Started',
    trigger: 'Planned: when either participant taps Start face-off while the other device is backgrounded.',
    title: 'Face-off starting',
    body: 'Your opponent started the count-in. Jump back in.',
    category: 'face-off',
    deliveryMethod: 'fcm-remote',
    source: 'Planned Firebase Function',
    dataKeys: ['type', 'challengeId', 'token', 'deepLink'],
    opensInto: 'Face-off recording count-in',
    configDocId: 'parrot-faceoff-started',
    notes: 'Not implemented yet. Current clients observe startedAt live when foregrounded.',
  },
  {
    id: 'parrot-first-speech-nudge',
    name: 'First Speech Nudge',
    trigger: '24 hours after signup when no completed speech exists.',
    title: 'Your first 60-second speech is waiting',
    body: 'Open Parrot, grab a prompt, and speak for sixty seconds.',
    category: 'practice',
    deliveryMethod: 'email',
    source: 'Parrot-web - EmailSequencesAdmin / Brevo',
    dataKeys: ['firstName', 'practiceUrl'],
    opensInto: 'Practice tab / marketing fallback',
    configDocId: 'parrot-first-speech-nudge-v1',
  },
  {
    id: 'parrot-score-ready',
    name: 'Score Ready',
    trigger: 'When a speech score finishes processing.',
    title: 'Your Parrot score is ready',
    body: 'Open Parrot to see the score recap, transcript highlights, and what to improve in your next speech.',
    category: 'progress',
    deliveryMethod: 'email',
    source: 'Parrot-web - EmailSequencesAdmin / Brevo',
    dataKeys: ['firstName', 'score', 'prompt', 'practiceUrl'],
    opensInto: 'Progress / score reveal context',
    configDocId: 'parrot-score-ready-v1',
  },
  {
    id: 'parrot-streak-rescue',
    name: 'Streak Rescue',
    trigger: 'When a user has not practiced today and their streak is at risk.',
    title: 'Keep your speaking streak alive',
    body: 'Your streak is still alive today. One short speech keeps the chain moving.',
    category: 'practice',
    deliveryMethod: 'email',
    source: 'Parrot-web - EmailSequencesAdmin / Brevo',
    dataKeys: ['firstName', 'currentStreak', 'practiceUrl'],
    opensInto: 'Practice tab',
    configDocId: 'parrot-streak-save-v1',
  },
  {
    id: 'parrot-level-up',
    name: 'Level Up',
    trigger: 'After reaching the next parrot evolution level.',
    title: 'You leveled up to {{levelName}}',
    body: 'You reached {{levelName}}. Keep building confidence with one completed speech at a time.',
    category: 'progress',
    deliveryMethod: 'email',
    source: 'Parrot-web - EmailSequencesAdmin / Brevo',
    dataKeys: ['firstName', 'levelName', 'totalSpeeches', 'practiceUrl'],
    opensInto: 'Progress tab',
    configDocId: 'parrot-level-up-v1',
  },
  {
    id: 'parrot-trial-ending',
    name: 'Trial Ending',
    trigger: '24 hours before a trial expires.',
    title: 'Your Parrot trial ends soon',
    body: 'Keep your speech gym open so your practice speeches, scores, and streaks continue.',
    category: 'subscription',
    deliveryMethod: 'email',
    source: 'Parrot-web - EmailSequencesAdmin / Brevo',
    dataKeys: ['firstName', 'trialEndsAt', 'subscriptionUrl'],
    opensInto: 'Subscription screen',
    configDocId: 'parrot-trial-ending-v1',
  },
  {
    id: 'parrot-winback',
    name: 'Inactivity Winback',
    trigger: '3, 7, and 14 days after the last completed speech.',
    title: 'A fresh prompt is waiting in Parrot',
    body: 'A fresh prompt is waiting when you are ready.',
    category: 'practice',
    deliveryMethod: 'email',
    source: 'Parrot-web - EmailSequencesAdmin / Brevo',
    dataKeys: ['firstName', 'daysInactive', 'practiceUrl'],
    opensInto: 'Practice tab',
    configDocId: 'parrot-winback-v1',
  },
];

const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  practice: 'Practice',
  'face-off': 'Face-off',
  progress: 'Progress',
  subscription: 'Subscription',
  system: 'System',
};

const DELIVERY_LABELS: Record<DeliveryMethod, string> = {
  local: 'Local',
  'fcm-remote': 'FCM push',
  email: 'Email',
  'scheduled-function': 'Scheduled',
};

function configDocId(sequence: NotificationSequenceRow) {
  return sequence.configDocId || sequence.id;
}

export const NotificationSequencesAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [enabledById, setEnabledById] = useState<Record<string, boolean>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [message, setMessage] = useState<BannerMessage | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<'all' | NotificationCategory>('all');
  const [deliveryFilter, setDeliveryFilter] = useState<'all' | DeliveryMethod>('all');

  useEffect(() => {
    const loadConfig = async () => {
      if (!db) return;
      const next: Record<string, boolean> = {};
      try {
        for (const sequence of NOTIFICATION_SEQUENCES) {
          const snap = await getDoc(doc(db, 'notification-sequence-config', configDocId(sequence)));
          const data = snap.exists() ? snap.data() : {};
          next[sequence.id] = typeof data.enabled === 'boolean' ? data.enabled : true;
        }
        setEnabledById(next);
      } catch (error: any) {
        setMessage({ type: 'error', text: error?.message || 'Failed to load notification sequence config.' });
      }
    };
    loadConfig();
  }, []);

  const filteredSequences = useMemo(() => {
    return NOTIFICATION_SEQUENCES.filter((sequence) => {
      if (categoryFilter !== 'all' && sequence.category !== categoryFilter) return false;
      if (deliveryFilter !== 'all' && sequence.deliveryMethod !== deliveryFilter) return false;
      return true;
    });
  }, [categoryFilter, deliveryFilter]);

  const stats = useMemo(() => {
    const enabled = NOTIFICATION_SEQUENCES.filter((sequence) => enabledById[sequence.id] ?? true).length;
    const remote = NOTIFICATION_SEQUENCES.filter((sequence) => sequence.deliveryMethod === 'fcm-remote').length;
    const email = NOTIFICATION_SEQUENCES.filter((sequence) => sequence.deliveryMethod === 'email').length;
    return { enabled, remote, email, total: NOTIFICATION_SEQUENCES.length };
  }, [enabledById]);

  const toggleEnabled = async (sequence: NotificationSequenceRow, enabled: boolean) => {
    if (!db) return;
    setSavingId(sequence.id);
    setMessage(null);
    try {
      await setDoc(
        doc(db, 'notification-sequence-config', configDocId(sequence)),
        {
          id: configDocId(sequence),
          sequenceId: sequence.id,
          name: sequence.name,
          category: sequence.category,
          deliveryMethod: sequence.deliveryMethod,
          enabled,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || 'admin',
        },
        { merge: true }
      );
      setEnabledById((prev) => ({ ...prev, [sequence.id]: enabled }));
      setMessage({ type: 'success', text: `${sequence.name} ${enabled ? 'enabled' : 'paused'}.` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Failed to save notification sequence config.' });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Notification Sequences</div>
            <div className="text-xs text-neutral-500">
              Push, local, and email notification surfaces Parrot can send.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button onClick={signOutUser} className="text-xs text-neutral-600 underline hover:text-neutral-900">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <AdminTabs current="notificationSequences" />
        </div>

        {message && (
          <div
            className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
              message.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : message.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Tracked" value={stats.total.toLocaleString()} />
          <StatCard label="Enabled" value={stats.enabled.toLocaleString()} />
          <StatCard label="Push" value={stats.remote.toLocaleString()} />
          <StatCard label="Email" value={stats.email.toLocaleString()} />
        </div>

        <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-neutral-900">Sequence Registry</div>
              <div className="text-xs text-neutral-500 mt-1">
                Config saves to <span className="font-mono">notification-sequence-config</span>. Delivery logs save to{' '}
                <span className="font-mono">notification-logs</span>.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value as 'all' | NotificationCategory)}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-medium"
              >
                <option value="all">All categories</option>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <select
                value={deliveryFilter}
                onChange={(event) => setDeliveryFilter(event.target.value as 'all' | DeliveryMethod)}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-medium"
              >
                <option value="all">All delivery</option>
                {Object.entries(DELIVERY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Sequence</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Trigger</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">Delivery</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filteredSequences.map((sequence) => {
                  const enabled = enabledById[sequence.id] ?? true;
                  const saving = savingId === sequence.id;
                  return (
                    <tr key={sequence.id} className={enabled ? 'hover:bg-neutral-50' : 'bg-neutral-50/70 hover:bg-neutral-50'}>
                      <td className="px-6 py-4 min-w-[240px]">
                        <div className="font-semibold text-neutral-900">{sequence.name}</div>
                        <div className="font-mono text-[11px] text-neutral-400 mt-1">{sequence.id}</div>
                        <div className="mt-2">
                          <span className="inline-flex rounded-full bg-neutral-100 border border-neutral-200 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
                            {CATEGORY_LABELS[sequence.category]}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-neutral-600 min-w-[280px]">
                        {sequence.trigger}
                        {sequence.notes ? <div className="text-xs text-neutral-400 mt-2">{sequence.notes}</div> : null}
                      </td>
                      <td className="px-6 py-4 text-neutral-600 min-w-[280px]">
                        <div className="font-semibold text-neutral-900">{sequence.title}</div>
                        <div className="text-xs text-neutral-500 mt-1">{sequence.body}</div>
                        <div className="text-[11px] text-neutral-400 mt-2">
                          Data: {sequence.dataKeys.join(', ')}
                        </div>
                      </td>
                      <td className="px-6 py-4 min-w-[220px]">
                        <div className="font-semibold text-neutral-800">{DELIVERY_LABELS[sequence.deliveryMethod]}</div>
                        <div className="text-xs text-neutral-500 mt-1">{sequence.source}</div>
                        <div className="text-xs text-neutral-400 mt-1">Opens: {sequence.opensInto}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => toggleEnabled(sequence, !enabled)}
                          disabled={saving}
                          className={`inline-flex min-w-[92px] justify-center rounded-lg px-3 py-2 text-xs font-semibold transition disabled:opacity-50 ${
                            enabled
                              ? 'bg-neutral-900 text-white hover:bg-neutral-800'
                              : 'bg-white border border-neutral-200 text-neutral-600 hover:border-neutral-400'
                          }`}
                        >
                          {saving ? 'Saving...' : enabled ? 'Enabled' : 'Paused'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-neutral-200 rounded-xl px-4 py-3">
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold">{label}</div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);
